<template>
	<div class="platform-dashboard" v-if="!noAccess">
		<h2 class="content-block header-container">
			<span>Platform Dashboard</span>
			<DxSelectBox class="sort-selector" displayExpr="text" valueExpr="value" v-model:value="cardSortMode" @valueChanged="sortDashboardCards"
				:items="[
					{ text: 'Sort by Name', value: 'name' },
					{ text: 'Sort by First Created', value: 'first' },
					{ text: 'Sort by Recent', value: 'recent' },
					{ text: 'Sort by Largest', value: 'biggest' }
				]"
			/>
		</h2>
		<div class="content-block dx-card dashboard-sparklines">
			<div v-if="sparklinePageHits.length > 0" style="width: 250px;">
				<DxSparkline :data-source="sparklinePageHits" type="area" value-field="count" argument-field="date" :show-min-max="true" :min-value="1" :size="{ width: 250, height: 60 }" />
				<div style="text-align: center; font-size: 0.8em; font-weight: bold; margin-top: 5px;">Page views last month {{ sparklineTaglines.pageHits }}</div>
			</div>
			<div v-if="sparklineVisitors.length > 0" style="width: 250px;">
				<DxSparkline :data-source="sparklineVisitors" type="area" value-field="count" argument-field="date" :show-min-max="true" :min-value="1" :size="{ width: 250, height: 60 }" />
				<div style="text-align: center; font-size: 0.8em; font-weight: bold; margin-top: 5px;">Unique visitors last month {{ sparklineTaglines.visitors }}</div>
			</div>
			<div v-if="sparklineCTAs.length > 0" style="width: 250px;">
				<DxSparkline :data-source="sparklineCTAs" type="area" value-field="count" argument-field="date" :show-min-max="true" :min-value="1" :size="{ width: 250, height: 60 }" />
				<div style="text-align: center; font-size: 0.8em; font-weight: bold; margin-top: 5px;">CTA clicks last month {{ sparklineTaglines.ctas }}</div>
			</div>
			<div v-if="sparklineRegistrations.length > 0" style="width: 250px;">
				<DxSparkline :data-source="sparklineRegistrations" type="area" value-field="count" argument-field="date" :show-min-max="true" :min-value="1" :size="{ width: 250, height: 60 }" />
				<div style="text-align: center; font-size: 0.8em; font-weight: bold; margin-top: 5px;">Registrations last month {{ sparklineTaglines.registrations }}</div>
			</div>
		</div>
		<div style="display: flex;">
			<div>
				<div class="content-block dx-card" style="padding: 10px;">
					<div style="max-width: 450px; margin: 0 auto;">
						<DxFunnel :data-source="funnelOnboarding" argument-field="stage" value-field="count" palette="Soft Pastel" :sort-data="false" :inverted="false">
							<DxTitle text="Onboarding Funnel (30-day window)" />
							<DxTooltip :enabled="true" :customize-tooltip="funnelTooltip" />
							<DxLegend :visible="false" />
							<DxLabel :visible="true" position="inside" :customize-text="funnelLabel" background-color="transparent" />
						</DxFunnel>
					</div>
				</div>
				<div class="content-block dx-card" style="padding: 10px;">
					<div style="max-width: 450px; margin: 0 auto;">
						<DxFunnel :data-source="funnelOnboarding" argument-field="stage" value-field="count" palette="Soft Pastel" :sort-data="false" :inverted="false">
							<DxTitle text="Onboarding Funnel (30-day window)" />
							<DxTooltip :enabled="true" :customize-tooltip="funnelTooltip" />
							<DxLegend :visible="false" />
							<DxLabel :visible="true" position="inside" :customize-text="funnelLabel" background-color="transparent" />
						</DxFunnel>
					</div>
				</div>
			</div>
			<div style="flex: 1 0;">
				<div class="content-block dx-card" style="padding: 10px;">
					Main Display
				</div>
			</div>
		</div>
	</div>
	<div v-else class="content-block">
		<div class="placeholder-card">
			<i class="dx-icon-warning"></i>
			<div class="title">No access</div>
			<div class="description">You do not have access to this page. Try logging in again.</div>
		</div>
	</div>
</template>

<script setup>
/* eslint-disable */
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxSparkline } from 'devextreme-vue/sparkline';
import DxFunnel, { DxLabel, DxLegend, DxTooltip, DxTitle } from 'devextreme-vue/funnel';
import CustomStore from 'devextreme/data/data_source';
import { onMounted, inject, ref, nextTick } from 'vue';
import DashboardSurveycard from '@/components/dashboard-surveycard.vue';

const noAccess = ref(false);				// Simple access warning since we don't have a good way to handle this yet.

const cardSortMode = ref('biggest');		// Sort cards by 'name' or 'first' or 'recent' or 'biggest'.
const sparklinePageHits = ref([]);			// Array of simple data used in sparkline for total count of page views per last month.
const sparklineVisitors = ref([]);			// Array of simple data used in sparkline for total count of visitors per last month.	
const sparklineCTAs = ref([]);				// Array of simple data used in sparkline for total count of CTA clicks per last month.
const sparklineRegistrations = ref([]);		// Array of simple data used in sparkline for total count of registrations per last month.
const sparklineTaglines = ref({				// Taglines for the sparklines. These are displayed below with the label.
	pageHits: '',
	visitors: '',
	ctas: '',
	registrations: '',
});

// Build a fixed daily series ending yesterday, filling missing dates with zeros.
const formatDateYMD = (date) => {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
};
const buildSparklineDays = (sourceMap, numDays = 30) => {
	const today = new Date();
	const result = [];
	const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
	const start = new Date(end);
	start.setDate(end.getDate() - (numDays - 1));
	const cursor = new Date(start);
	while (cursor <= end) {
		const key = formatDateYMD(cursor);
		const value = sourceMap && key in sourceMap ? sourceMap[key] : 0;
		result.push({ date: key, count: value });
		cursor.setDate(cursor.getDate() + 1);
	}
	return result;
};
const summarizeSparklineData = (data) => {
	return data.reduce((sum, item) => sum + item.count, 0).toLocaleString();
};

const $http = inject('$http');
onMounted(async () => {
	// TODO: Get data KPIs, etc.
	let statsPlatform = await $http.get('/stats_platform');
	const pageHitsMap = statsPlatform.data && statsPlatform.data.pageHits ? statsPlatform.data.pageHits : {};
	const visitorsMap = statsPlatform.data && statsPlatform.data.visitors ? statsPlatform.data.visitors : {};
	const ctasMap = statsPlatform.data && statsPlatform.data.ctaClicks ? statsPlatform.data.ctaClicks : {};
	const registrationsMap = statsPlatform.data && statsPlatform.data.registrations ? statsPlatform.data.registrations : {};

	// Sparkline data: always show the last 30 days ending yesterday, filling missing dates with 0.
	sparklinePageHits.value = buildSparklineDays(pageHitsMap, 30);
	sparklineVisitors.value = buildSparklineDays(visitorsMap, 30);
	sparklineCTAs.value = buildSparklineDays(ctasMap, 30);
	sparklineRegistrations.value = buildSparklineDays(registrationsMap, 30);
	sparklineTaglines.value.pageHits = statsPlatform.data.pageHits ? `(${summarizeSparklineData(sparklinePageHits.value)})` : '';
	sparklineTaglines.value.visitors = statsPlatform.data.visitors ? `(${summarizeSparklineData(sparklineVisitors.value)})` : '';
	sparklineTaglines.value.ctas = statsPlatform.data.ctaClicks ? `(${summarizeSparklineData(sparklineCTAs.value)})` : '';
	sparklineTaglines.value.registrations = statsPlatform.data.registrations ? `(${summarizeSparklineData(sparklineRegistrations.value)})` : '';
});


const sortDashboardCards = () => {};	// TODO: Implement

// Mock 6-stage process data for the funnel
const funnelOnboarding = ref([
	{ stage: 'Visited', count: 10 },
	{ stage: 'Clicked CTA', count: 8 },
	{ stage: 'Reached Midform', count: 7 },
	{ stage: 'Submitted Form', count: 7 },
	{ stage: 'Verified Email', count: 5 },
	{ stage: 'Logged In', count: 4 },
]);
const funnelTooltip = (info) => {
	const item = info && info.point && info.point.data ? info.point.data : null;
	if (!item || funnelOnboarding.value.length === 0) return { text: '' };
	const base = funnelOnboarding.value[0].count || 1;
	const pct = (item.count / base) * 100;
	return { text: `${item.stage}: ${item.count.toLocaleString()} (${pct.toFixed(1)}%)` };
};
const funnelLabel = (info) => {
    const data = info && info.item && info.item.data ? info.item.data : null;
    if (!data) return '';
    return `${data.stage}: ${data.count.toLocaleString()}`;
};

</script>

<style scoped lang="scss">
.platform-dashboard {
	.header-container {
		display: flex;
		justify-content: space-between;
		align-items: center;
		flex-wrap: wrap;
		gap: 1rem;
	}
	.sort-selector {
		min-width: 200px;
		max-width: 300px;
	}
	.dashboard-sparklines {
		padding: 10px 10px;
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
		justify-content: center;
	}
}
</style>
